import { useAuiState } from '@assistant-ui/react';
import { useEffect, useId, useRef } from 'react';

import { reportAgentRunTitleState } from './agent-run-title-status.js';
import { isTabInForeground } from './tab-foreground.js';

export interface UseAgentRunTitleStatusOptions {
	/** Whether this surface may mark the tab title. */
	enabled: boolean;
}

/**
 * Marks the tab title while the active thread's run is in flight, and leaves a
 * different mark behind when a run finishes without the user seeing it. The title
 * is left untouched otherwise. Must be called inside an `AssistantRuntimeProvider`.
 *
 * @param options.enabled - Whether this surface may mark the tab title.
 */
export function useAgentRunTitleStatus({ enabled }: UseAgentRunTitleStatusOptions): void {
	const isRunning = useAuiState((s) => s.thread.isRunning);
	const conversationId = useAuiState((s) => s.threadListItem.remoteId);
	// One entry per mount, so two panels on screen at once aggregate rather than
	// overwrite, and switching threads does not orphan an entry.
	const reporterKey = useId();

	// Paired with the conversation it was observed on: switching from a running
	// thread to an idle one is two different threads' states, not one thread
	// finishing, and must not leave an unseen mark.
	const lastObserved = useRef<{ conversationId: string | undefined; isRunning: boolean }>({
		conversationId,
		isRunning: false,
	});

	// Driving an external system (the document title) off run state — the justified
	// use of an effect.
	useEffect(() => {
		const previous = lastObserved.current;
		lastObserved.current = { conversationId, isRunning };

		if (!enabled) {
			reportAgentRunTitleState(reporterKey, 'idle');
			return;
		}
		if (isRunning) {
			reportAgentRunTitleState(reporterKey, 'working');
			return;
		}
		const hasJustFinished = previous.conversationId === conversationId && previous.isRunning;
		const isUnseen = hasJustFinished && !isTabInForeground();
		reportAgentRunTitleState(reporterKey, isUnseen ? 'unseen' : 'idle');
	}, [reporterKey, conversationId, isRunning, enabled]);

	// Separate from the reporting effect so a dependency change cannot briefly
	// withdraw a mark that is still current.
	useEffect(() => () => reportAgentRunTitleState(reporterKey, 'idle'), [reporterKey]);
}
